import { Button } from 'antd'
import { motion } from 'framer-motion'
import i18next from 'i18next'
import { ArrowRight, Rocket } from 'phosphor-react'
import React, { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import HelmetComponent from '../../../Component/Helmet'
import j2bdLogo from '../../../assets/images/j2bd.svg'
import holloLogo from '../../../assets/images/loghollo.png'
import { setPreferredLanguage } from '../../../redux/reducers/talentSlice'
import { sendAmplitudeData } from '../../../services/amplitude'
import { checkFeatureFlag } from '../../../utils/featureFlag'
import Signup from '../SignUp/SignUp'
import * as s from './OnboardingPage.styles'

import { handleInitStatus } from '../../../api/talents.api'
import { FeathersContext } from '../../../services/feathers'
import { Provider, StepContext } from '../../../services/stepContext'
import {
  OnboardingProvider,
  onboardingContext,
} from '../Home/OnboardingProvider'
import LayoutOnboardingPages from '../components/LayoutOnboardingPages/LayoutOnboardingPages'
import {
  FinalStep,
  FirstStep,
  FourthStep,
  SecondStep,
  ThirdStep,
} from './Steps/Steps'

const propTypes = {
  // setVisible: func.isRequired,
}

const variants = {
  animate: {
    y: 0,
    opacity: 1,
  },
  initial: {
    y: 600,
    opacity: 0,
  },
  exit: {
    opacity: 0,
    y: -600,
    transition: { delay: 0.3, duration: 0.5 },
  },
  transition: {
    stiffness: 75,
    duration: 0.6,
  },
}

const OnboardingPage = () => {
  const stepContext = useContext(StepContext)

  const { t } = useTranslation(['common'], { useSuspense: false })
  const { isLoading, setIsLoading, step, setStep, progress, setProgress } =
    useContext(onboardingContext)
  const feathers = useContext(FeathersContext)
  const dispatch = useDispatch()
  const history = useHistory()
  const {
    researchStatus,
    company,
    jobDomain,
    id,
    firstname,
    location,
    contractTypes,
    experience,
    schoolLevel,
    subJob,
    sector,
    subSector,
    salaryRange,
    candidateIds,
    remoteWorking,
  } = useSelector((state) => state.talent)

  useEffect(() => {
    const { language } = navigator

    if (language === 'fr-FR') {
      dispatch(setPreferredLanguage(language))
      i18next.changeLanguage('fr')
    } else {
      i18next.changeLanguage('en')
    }
  }, [])

  // create useEffect who listen step and update stepContext value
  useEffect(() => {
    stepContext.setValue(step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const disabled = useMemo(() => {
    if (step === 1) {
      return false
    }

    if (step === 2) {
      if (contractTypes?.length > 0 && schoolLevel && salaryRange) {
        return false
      }
    }

    if (step === 3) {
      if (location.city !== null && researchStatus) {
        return false
      }
    }

    if (step === 4) {
      if (sector && experience) {
        return false
      }
    }

    if (step === 5) {
      if (jobDomain) {
        return false
      }
    }

    if (step === 6) {
      return false
    }
    return true
  }, [
    step,
    contractTypes,
    schoolLevel,
    salaryRange,
    location.city,
    researchStatus,
    sector,
    experience,
    jobDomain,
  ])

  const onboardingMutation = useMutation(
    async () =>
      feathers.service('talents').patch(id, {
        researchStatus,
        researchStatusUpdatedAt: new Date(),
        jobDomain,
        subJob,
        sector,
        subSector,
        experience,
        schoolLevel,
        location,
        contractTypes,
        salaryRange,
        remoteWorking,
        active: true,
      }),
    {
      onError: (error) => {
        console.error(error)
      },
    },
  )

  const handleNextStep = () => {
    setIsLoading(true)
    sendAmplitudeData('Talent - Finish Onboarding Step', {
      Step_Index: step,
    })

    if (step === 6) {
      handleInitStatus(candidateIds, 'finished')
      onboardingMutation.mutate()

      history.replace('/app/jobs')
      return
    }

    setTimeout(() => {
      setStep(step + 1)
      setProgress(progress + 20)
      setIsLoading(false)
    }, 500)
  }

  const handleGoBack = () => {
    setStep((prevState) => prevState - 1)
    setProgress((prevState) => prevState - 20)
  }

  return (
    <>
      <HelmetComponent
        faviconUrl={checkFeatureFlag ? j2bdLogo : holloLogo}
        name={checkFeatureFlag ? 'J2BD Partners' : 'Hollo'}
      />
      <s.OnboardingPageWrapper>
        <motion.div className="main-container">
          {step === 1 && (
            <Signup
              handleNextStep={handleNextStep}
              firstname={firstname}
              isLoading={isLoading}
            />
          )}
          {step === 2 && <FirstStep />}
          {step === 3 && <SecondStep />}
          {step === 4 && <ThirdStep />}
          {step === 5 && <FourthStep />}
          {step === 6 && <FinalStep />}
          {/* Button to change step onboarding */}
          <div
            style={{
              display: 'flex',
              justifyContent: step === 2 ? 'flex-end' : 'space-between',
              alignItems: 'center',
              width: '100%',
              marginTop: '2rem',
              gap: '1rem',
            }}
          >
            {step > 2 && (
              <Button
                type="link"
                className="link-element"
                style={{
                  width: 'fit-content',
                  borderBottom: '1px solid #A2B4F2',
                }}
                onClick={handleGoBack}
              >
                {t('common:previous')}
              </Button>
            )}
            {step >= 2 && (
              <Button
                style={{
                  width: 'fit-content',
                }}
                disabled={disabled}
                className={disabled ? 'grey-btn' : 'basic-btn'}
                onClick={handleNextStep}
              >
                {step === 6 ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItem: 'center',
                      gap: '12px',
                      fontWeight: '600',
                      lineHeight: '24px',
                      alignItems: 'center',
                    }}
                  >
                    {t('common:letsGo')}
                    <Rocket size={20} />
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItem: 'center',
                      gap: '12px',
                      fontWeight: '600',
                      lineHeight: '24px',
                      alignItems: 'center',
                    }}
                  >
                    {t('common:next')}
                    <ArrowRight size={24} weight="bold" />
                  </div>
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </s.OnboardingPageWrapper>
    </>
  )
}

OnboardingPage.propTypes = propTypes

const Wrapper = () => (
  <Provider>
    <OnboardingProvider>
      <LayoutOnboardingPages type="signup">
        <OnboardingPage />
      </LayoutOnboardingPages>
    </OnboardingProvider>
  </Provider>
)
export default Wrapper
